<template>
    <div>
        <q-card-section class="q-mx-xs">
            <div class="row text-weight-medium">
                <q-icon name="fa-solid fa-mobile-notch" class="mfa-icon" />
                {{ $t('Authenticator App') }}

                <q-badge
                    v-if="isMFAMethodEnabled('app')"
                    color="positive"
                    class="q-ml-sm"
                >
                    <q-icon name="check" color="white" class="q-mr-xs" />
                    {{ $t('Enabled') }}
                </q-badge>
            </div>

            <div class="row items-center" v-if="!isMFAMethodEnabled('app')">
                <div
                    class="col-md-10 col-sm-12 text-secondary q-py-sm"
                    v-if="methodToEnable !== 'app'"
                >
                    {{
                        $t(
                            'Use an authenticator app (e.g. Microsoft Authenticator, Authy) to obtain a one-time password (OTP).'
                        )
                    }}
                </div>
                <div class="col-md-2 col-sm-12 text-right">
                    <q-btn
                        v-show="methodToEnable !== 'app'"
                        :color="hasActiveMethod ? 'grey' : 'primary'"
                        outline
                        @click="showMFAMethodActivationSteps('app')"
                        :disabled="hasActiveMethod"
                        id="enable-app-mfa"
                    >
                        {{ $t('Enable') }}
                        <q-tooltip
                            v-if="hasActiveMethod && methodToEnable !== 'app'"
                        >
                            {{
                                $t(
                                    'You can only enable one MFA method at a time. Please disable the current method before enabling a new one.'
                                )
                            }}
                        </q-tooltip>
                    </q-btn>
                </div>
            </div>
            <div
                v-if="methodToEnable === 'app' && !hasActiveMethod"
                class="row items-center q-pt-lg q-col-gutter-lg"
            >
                <div class="col-md-6 col-sm-12">
                    <div class="row text-secondary justify-center text-center">
                        {{
                            $t(
                                'Scan the QR code below with your Authenticator App:'
                            )
                        }}
                    </div>
                    <div class="row items-center justify-center q-my-md">
                        <vue-qrcode
                            v-if="authenticatorOTPSecret"
                            :value="authenticatorOTPSecret"
                            type="image/png"
                            :color="{
                                dark: '#000000ff',
                                light: '#ffffffff'
                            }"
                            :data-test="
                                isDevelopmentMode
                                    ? authenticatorOTPSecret
                                    : null
                            "
                            id="app-qr-code"
                        />
                    </div>
                </div>
                <div class="col-12" v-if="$q.screen.lt.md">
                    <q-separator />
                </div>
                <div class="col-md-6 col-sm-12">
                    <div class="row justify-center">
                        <span class="text-secondary">
                            {{
                                $t(
                                    'Enter the one-time password generated by your Authenticator App:'
                                )
                            }}
                        </span>
                    </div>
                    <div class="row justify-center full-width">
                        <one-time-password-input
                            class="q-my-md"
                            :showBackupCodes="false"
                            @passwordIsValid="val => handleEnableMfa(val)"
                            @update:modelValue="otp => (oneTimePassword = otp)"
                            :key="`otp-${submitCounter}`"
                        />
                    </div>
                    <div class="row q-gutter-x-sm justify-center">
                        <q-btn
                            @click="methodToEnable = null"
                            :label="$t('Cancel')"
                            text-color="primary"
                            color="white"
                            outline
                        />
                        <q-btn
                            :label="$t('Activate')"
                            @click="enableMFA"
                            color="primary"
                            id="activate-button"
                            :disable="!isOTPValid"
                        />
                    </div>
                </div>
            </div>
            <div
                v-if="isMFAMethodEnabled('app')"
                class="row items-center justify-between"
            >
                <div class="q-py-sm q-gutter-sm">
                    <div class="text-secondary">
                        {{
                            $t(
                                '{method_name} verification method is enabled for your account.',
                                {
                                    method_name: $t('Authenticator App')
                                }
                            )
                        }}
                    </div>
                    <div
                        class="text-primary cursor-pointer"
                        @click="showBackupCodesDialog = true"
                    >
                        {{ $t('Regenerate backup codes') }}
                        <q-icon
                            name="fa-regular fa-question-circle"
                            size="xs"
                            color="secondary"
                            class="q-ml-sm"
                        >
                            <q-tooltip>
                                {{
                                    $t(
                                        'Backup codes are used to access your account if you lose access to your authenticator app.'
                                    )
                                }}
                            </q-tooltip>
                        </q-icon>
                    </div>
                </div>
                <div>
                    <q-btn
                        @click="showDisableMfaDialog = true"
                        color="secondary"
                        outline
                        id="disable-app-mfa"
                    >
                        {{ $t('Disable') }}
                    </q-btn>
                </div>
            </div>
        </q-card-section>

        <q-separator class="q-mx-md" />

        <q-card-section class="q-mx-xs">
            <div class="row text-weight-medium">
                <q-icon name="fa-regular fa-envelope" class="mfa-icon" />
                {{ $t('Email') }}
                <q-badge
                    v-if="isMFAMethodEnabled('email')"
                    color="positive"
                    class="q-ml-sm"
                >
                    <q-icon name="check" color="white" class="q-mr-xs" />
                    {{ $t('Enabled') }}
                </q-badge>
            </div>

            <div class="row items-center" v-if="!isMFAMethodEnabled('email')">
                <div
                    class="col-md-10 col-sm-12 text-secondary q-py-sm"
                    v-if="methodToEnable !== 'email'"
                >
                    {{
                        $t(
                            "Use your account's email address to obtain a one-time password (OTP)."
                        )
                    }}
                </div>
                <div class="col-md-2 col-sm-12 text-right">
                    <q-btn
                        v-show="methodToEnable !== 'email'"
                        class="text-uppercase"
                        :color="hasActiveMethod ? 'grey' : 'primary'"
                        outline
                        @click="showMFAMethodActivationSteps('email')"
                        :disabled="hasActiveMethod"
                        id="enable-email-mfa"
                    >
                        {{ $t('Enable') }}
                        <q-tooltip
                            v-if="hasActiveMethod && methodToEnable !== 'email'"
                        >
                            {{
                                $t(
                                    'You can only enable one MFA method at a time. Please disable the current method before enabling a new one.'
                                )
                            }}
                        </q-tooltip>
                    </q-btn>
                </div>
            </div>
            <div
                v-if="methodToEnable === 'email' && !hasActiveMethod"
                class="row items-center q-pt-lg q-col-gutter-lg"
            >
                <div class="col">
                    <div class="row justify-center">
                        <span class="text-secondary">
                            {{
                                $t(
                                    'Enter the one-time password sent to your email address:'
                                )
                            }}
                        </span>
                    </div>

                    <div class="row justify-center full-width">
                        <one-time-password-input
                            class="q-my-md"
                            :showBackupCodes="false"
                            @passwordIsValid="val => handleEnableMfa(val)"
                            @update:modelValue="otp => (oneTimePassword = otp)"
                            :key="`otp-${submitCounter}`"
                        />
                    </div>
                    <div class="row justify-center full-width q-mb-sm">
                        <p class="text-secondary">
                            {{ $t("Didn't receive code?") }}
                            <!-- Clickable text -->
                            <span
                                class="text-primary cursor-pointer"
                                @click="sendOTPToEmail()"
                                :class="{
                                    disabled: emailOTPCoolDownSeconds > 0
                                }"
                                id="resend-email-otp-enabling"
                            >
                                {{ $t('Resend code') }}
                                <span v-if="emailOTPCoolDownSeconds > 0">
                                    ({{ emailOTPCoolDownSeconds }})
                                </span>
                            </span>
                        </p>
                    </div>
                    <div class="row q-gutter-x-sm justify-center q-mb-md">
                        <q-btn
                            @click="methodToEnable = null"
                            :label="$t('Cancel')"
                            text-color="primary"
                            color="white"
                            outline
                        />
                        <q-btn
                            :label="$t('Activate')"
                            @click="enableMFA"
                            color="primary"
                            id="activate-button"
                            :disable="!isOTPValid"
                        />
                    </div>
                </div>
            </div>
            <div
                v-if="isMFAMethodEnabled('email')"
                class="row items-center justify-between"
            >
                <div class="q-py-sm q-gutter-sm">
                    <div class="text-secondary">
                        {{
                            $t(
                                '{method_name} verification method is enabled for your account.',
                                {
                                    method_name: $t('Email')
                                }
                            )
                        }}
                    </div>
                    <div
                        class="text-primary cursor-pointer"
                        @click="
                            () => {
                                sendOTPToEmail();
                                showBackupCodesDialog = true;
                            }
                        "
                    >
                        {{ $t('Regenerate backup codes') }}
                        <q-icon
                            name="fa-regular fa-question-circle"
                            size="xs"
                            color="secondary"
                            class="q-ml-sm"
                        >
                            <q-tooltip>
                                {{
                                    $t(
                                        'Backup codes are used to access your account if you lose access to your authenticator app.'
                                    )
                                }}
                            </q-tooltip>
                        </q-icon>
                    </div>
                </div>
                <div>
                    <q-btn
                        @click="
                            () => {
                                sendOTPToEmail();
                                showDisableMfaDialog = true;
                            }
                        "
                        color="secondary"
                        outline
                        id="disable-email-mfa"
                    >
                        {{ $t('Disable') }}
                    </q-btn>
                </div>
            </div>
        </q-card-section>

        <!-- Backup codes dialog -->
        <backup-codes-dialog
            :show="showBackupCodesDialog"
            :method="activeMethod"
            :activationBackupCodes="backupCodes"
            :emailOTPCoolDownSeconds="emailOTPCoolDownSeconds"
            @resendCodes="activeMethod === 'email' ? sendOTPToEmail() : null"
            @close="closeBackupCodesDialog"
        />

        <!-- Disable MFA dialog -->
        <q-dialog v-model="showDisableMfaDialog">
            <q-card style="width: 400px">
                <q-bar class="bg-primary">
                    <div class="text-white">
                        {{
                            $t('Disable MFA ({method_name})', {
                                method_name: methodTypeToLabel[activeMethod]
                            })
                        }}
                    </div>
                    <q-space />
                    <q-btn
                        @click="showDisableMfaDialog = false"
                        class="text-white"
                        icon="close"
                        dense
                        flat
                    >
                        <q-tooltip>{{ $t('Close') }}</q-tooltip>
                    </q-btn>
                </q-bar>
                <q-banner
                    class="background-yellow border-yellow q-ma-md warning-banner"
                    inline-actions
                >
                    <template v-slot:avatar>
                        <q-icon
                            name="fa-solid fa-triangle-exclamation"
                            color="warning"
                        ></q-icon>
                    </template>
                    {{
                        $t(
                            "Disabling multi-factor authentication can risk your account's security."
                        )
                    }}
                </q-banner>
                <q-card-section>
                    <div class="row text-center text-secondary">
                        {{
                            $t(
                                'Enter the one-time password for this verification method or use a backup code to disable MFA:'
                            )
                        }}
                    </div>
                    <div class="row justify-center">
                        <one-time-password-input
                            class="q-my-md"
                            @update:modelValue="otp => (oneTimePassword = otp)"
                            @passwordIsValid="val => handleDisableMfa(val)"
                            :key="`otp-${submitCounter}`"
                        />
                    </div>

                    <div class="row justify-between">
                        <span class="text-secondary">
                            <!-- Clickable text -->
                            <span
                                class="text-primary cursor-pointer"
                                @click="sendOTPToEmail()"
                                :class="{
                                    disabled: emailOTPCoolDownSeconds > 0
                                }"
                                id="resend-email-otp-disabling"
                            >
                                {{ $t('Resend code') }}
                                <span v-if="emailOTPCoolDownSeconds > 0">
                                    ({{ emailOTPCoolDownSeconds }})
                                </span>
                            </span>
                        </span>
                        <span>
                            <q-btn
                                @click="showDisableMfaDialog = false"
                                :label="$t('Cancel')"
                                class="q-mr-sm"
                                text-color="primary"
                                color="white"
                                outline
                            />
                            <q-btn
                                @click="disableMFA"
                                :label="$t('Disable')"
                                color="primary"
                                id="disable-button"
                                :disable="!isOTPValid"
                            />
                        </span>
                    </div>
                </q-card-section>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import VueQrcode from 'vue-qrcode';
import OneTimePasswordInput from '@components/OneTimePasswordInput.vue';
import BackupCodesDialog from '@components/BackupCodesDialog.vue';
import { methodTypeToLabel } from '@pages/Authentication/utils.js';
import { mapActions } from 'pinia';
import { useSharedStore } from '@stores/shared.js';

export default {
    name: 'MFAPanel',
    emits: ['refreshMethods', 'mfaActivated'],
    components: { VueQrcode, OneTimePasswordInput, BackupCodesDialog },
    props: {
        activeMethod: {
            type: String,
            required: false,
            default: null
        }
    },
    data() {
        return {
            methodToEnable: null,
            oneTimePassword: '',
            showBackupCodesDialog: false,
            showDisableMfaDialog: false,
            isOTPValid: false,
            backupCodes: [],

            authenticatorOTPSecret: null, // only used for authenticator app
            emailOTPCoolDownSeconds: 0, // cooldown for sending email OTP
            emailOTPResendInterval: null,
            EMAIL_OTP_MAX_COOLDOWN_S: 60,
            submitCounter: 0, // Used to reset OTP input each time it is used

            methodTypeToLabel
        };
    },
    computed: {
        /**
         * Check if there is an active MFA method.
         * @return {Boolean} - True if there is an active MFA method.
         */
        hasActiveMethod() {
            return this.activeMethod !== null;
        },
        /**
         * Check if the environment is development. Show MFA secret only in development as this is
         * needed for E2E testing. However, this is not recommended for production for security reasons.
         * @return {Boolean} - True if the environment is development.
         */
        isDevelopmentMode() {
            return process.env.NODE_ENV === 'development';
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        /**
         * Reset the states for MFA.
         */
        reset() {
            this.methodToEnable = null;
            this.oneTimePassword = '';
            this.$emit('refreshMethods');
            this.showDisableMfaDialog = false;
            this.emailOTPCoolDownSeconds = 0;
            this.emailOTPResendInterval = null;
            this.authenticatorOTPSecret = null;
        },
        /**
         * Check if a MFA method is enabled.
         * @param {String} method - The MFA method.
         * @return {Boolean} - True if the MFA method is enabled.
         */
        isMFAMethodEnabled(method) {
            return this.activeMethod === method;
        },
        /**
         * Activate a MFA method.
         */
        async activateMFAMethod() {
            try {
                const response = await this.$axios.post(
                    `/auth/${this.methodToEnable}/activate/`
                );

                if (this.methodToEnable === 'app') {
                    this.authenticatorOTPSecret = response.data['details'];
                } else if (this.methodToEnable === 'email') {
                    this.startResendOTPCoolDown();
                    this.$notifySuccess(
                        this.$t('One-time password sent to your email.')
                    );
                }
            } catch (error) {
                this.$notifyError(this.$t('Failed to generate MFA secret'));
                console.error(error);
            }
        },
        /**
         * Handle MFA activation
         */
        async enableMFA() {
            // begin loading
            this.beginLoading(this.$t('Enabling MFA...'));
            try {
                const payload = {
                    code: this.oneTimePassword
                };
                const response = await this.$axios.post(
                    `/auth/${this.methodToEnable}/activate/confirm/`,
                    payload
                );
                this.backupCodes = response.data['backup_codes'];
                this.showBackupCodesDialog = true;
                this.clearResendOTPCoolDown();
                this.reset();
                this.$notifySuccess(this.$t('MFA enabled.'));
            } catch (e) {
                const error =
                    e?.response?.data && typeof e.response.data === 'object'
                        ? e.response.data
                        : {};
                this.oneTimePassword = '';
                if (error.code[0] === 'Code invalid or expired.') {
                    this.$notifyError(
                        this.$t('One-time password invalid or QR code expired.')
                    );
                } else {
                    this.$notifyError(this.$t('Failed to enable MFA'));
                }
            } finally {
                // stop loading
                this.stopLoading();
                this.submitCounter += 1;
            }
        },
        /**
         * Disable MFA.
         */
        async disableMFA() {
            this.showDisableMfaDialog = false;
            // begin loading
            this.beginLoading(this.$t('Disabling MFA...'));
            try {
                const payload = {
                    code: this.oneTimePassword
                };
                await this.$axios.post(
                    `/auth/${this.activeMethod}/deactivate/`,
                    payload
                );

                this.clearResendOTPCoolDown();
                this.reset();
                this.$notifySuccess(this.$t('MFA disabled.'));
            } catch (error) {
                this.$notifyError(
                    this.$t('Failed to disable MFA. Please try again.')
                );
                console.error(error);
            } finally {
                // stop loading
                this.stopLoading();
            }
        },
        /**
         * Show MFA method activation steps.
         * @param {String} method - MFA method to enable.
         */
        showMFAMethodActivationSteps(method) {
            this.methodToEnable = method;

            if (this.emailOTPResendInterval !== null) {
                return;
            }
            this.activateMFAMethod();
        },
        /**
         * Close the backup codes dialog and clear the backup codes.
         */
        closeBackupCodesDialog() {
            this.backupCodes = [];
            this.showBackupCodesDialog = false;
            this.$emit('mfaActivated');
        },
        /**
         * Handle OTP input to enable MFA.
         * @param {Boolean} isValid - True if OTP is valid.
         */
        handleEnableMfa(isValid) {
            this.isOTPValid = isValid;
            if (isValid) {
                this.enableMFA();
            }
        },
        /**
         * Handle OTP input to disable MFA.
         * @param {Boolean} isValid - True if OTP is valid.
         */
        handleDisableMfa(isValid) {
            this.isOTPValid = isValid;
            if (isValid) {
                this.disableMFA();
            }
        },
        /**
         * Send OTP to email.
         */
        async sendOTPToEmail() {
            // check if last code was sent 60 seconds ago
            if (this.emailOTPCoolDownSeconds > 0) {
                return;
            }

            this.startResendOTPCoolDown();

            await this.$axios
                .post(`/auth/code/request/`, {
                    method: 'email'
                })
                .then(() => {
                    this.$notifySuccess(
                        this.$t('One-time password sent to your email.')
                    );
                })
                .catch(() => {
                    this.$notifyError(
                        this.$t(
                            'Failed to send one-time password to your email.'
                        )
                    );
                });
        },
        /**
         * Clear resend code cooldown.
         */
        clearResendOTPCoolDown() {
            if (this.emailOTPResendInterval) {
                clearInterval(this.emailOTPResendInterval);
                this.emailOTPResendInterval = null;
            }
        },
        /**
         * Start resend code cooldown.
         */
        startResendOTPCoolDown() {
            this.clearResendOTPCoolDown();

            this.emailOTPCoolDownSeconds = this.EMAIL_OTP_MAX_COOLDOWN_S;
            this.emailOTPResendInterval = setInterval(() => {
                if (this.emailOTPCoolDownSeconds === 0) {
                    clearInterval(this.emailOTPResendInterval);
                    this.emailOTPResendInterval = null;
                    return;
                }
                this.emailOTPCoolDownSeconds -= 1;
            }, 1000);
        }
    }
};
</script>

<style scoped>
.mfa-icon {
    margin-right: 6px;
    height: 1.3em;
}
</style>
